import {
  Box,
  Button,
  Flex,
  Link,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Stack,
  Table,
  Tag,
  Tbody,
  Td,
  Text,
  Thead,
  Tr,
  type TableColumnHeaderProps,
} from "@chakra-ui/react";
import type { TFunction } from "i18next";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link as RouterLink, useSearchParams } from "react-router-dom";
import {
  useGetProcurementWorkloadQuery,
  useListProcurementWorkloadDeadlinesQuery,
  SortDirection,
  type PersonProcurementWorkloadDto,
} from "../../autogen/bff-api";
import { useLoggedInWithOrgContextState } from "../../common/auth/useLoggedInWithOrgContextState";
import { ColumnHeader } from "../../common/entity-table/ColumnHeader";
import { useApiError } from "../../common/errors/useApiError";
import { formatNumber } from "../../common/formatting/formatNumber";
import { HorizontalScrollBox } from "../../common/HorizontalScrollBox";
import { ListLoadError } from "../../common/ListLoadError";
import { DeadlineRow } from "../home/panels/DeadlineRow";
import {
  columnLabel,
  countOf,
  nextSort,
  parseWorkloadSort,
  sortedPeople,
  workloadColumns,
  workloadLink,
  workloadScopeText,
  type WorkloadColumn,
  type WorkloadSort,
  type WorkloadSortKey,
} from "./buyerWorkload";
import { sharedNames, sharesName } from "./taskWorkload";

const stickyNameColumn = {
  position: "sticky",
  left: 0,
  zIndex: 1,
  bg: "smBackground",
  minW: "10rem",
  maxW: "16rem",
  whiteSpace: "normal",
  borderRight: "1px solid",
  borderRightColor: "smBorder",
} as const;

const countTarget = {
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-end",
  w: "full",
  minH: "44px",
  px: "4",
  py: "3",
  textAlign: "right",
  fontSize: "sm",
  lineHeight: "inherit",
} as const;

export const ProcurementWorkload = () => {
  const { t, i18n } = useTranslation();
  const organizationId = useLoggedInWithOrgContextState().selectedOrg.id;
  const { message } = useApiError();
  const [params, setParams] = useSearchParams();
  const [deadlinesOf, setDeadlinesOf] = useState<PersonProcurementWorkloadDto | null>(null);
  const sort = parseWorkloadSort(params.get("workloadSort"), params.get("workloadOrder"));
  const { data, isLoading, isFetching, error, refetch } = useGetProcurementWorkloadQuery({ orgId: organizationId });

  const sortBy = (key: WorkloadSortKey) => {
    const next = nextSort(sort, key);
    setParams(
      (current) => {
        const updated = new URLSearchParams(current);
        if (next) {
          updated.set("workloadSort", next.key);
          updated.set("workloadOrder", next.direction);
        }
        return updated;
      },
      { replace: true },
    );
  };

  return (
    <Stack
      as="section"
      aria-labelledby="workload-heading"
      spacing="3"
      borderWidth="1px"
      borderColor="smBorder"
      borderRadius="lg"
      p="4"
      bg="smBackground"
      data-testid="procurement-workload"
    >
      <Box>
        <Text as="h2" id="workload-heading" fontWeight="semibold" color="smText">
          {t("Workload per buyer")}
        </Text>
        <Text fontSize="sm" color="smMuted">
          {t("As it stands today, whatever period is chosen above.")}
        </Text>
      </Box>
      {isLoading ? (
        <Spinner color="smPrimary" />
      ) : error || !data ? (
        <ListLoadError message={message(error)} onRetry={() => void refetch()} isRetrying={isFetching} />
      ) : (
        <>
          {data.people.length === 0 ? (
            <Text fontSize="sm" color="smMuted">
              {t("Nobody is responsible for a need, an active procurement, an active contract or a deadline yet.")}
            </Text>
          ) : (
            <WorkloadTable
              people={sortedPeople(data.people, sort, i18n.language)}
              columns={workloadColumns(data)}
              shared={sharedNames(data.people)}
              sort={sort}
              onSort={sortBy}
              organizationId={organizationId}
              onOpenDeadlines={setDeadlinesOf}
            />
          )}
          <Text fontSize="sm" color="smMuted">
            {t("Based on what you have access to: {{scope}}", { scope: workloadScopeText(t, data) })}
          </Text>
        </>
      )}
      {deadlinesOf && (
        <DeadlinesDialog person={deadlinesOf} organizationId={organizationId} onClose={() => setDeadlinesOf(null)} />
      )}
    </Stack>
  );
};

const WorkloadTable = ({
  people,
  columns,
  shared,
  sort,
  onSort,
  organizationId,
  onOpenDeadlines,
}: {
  people: PersonProcurementWorkloadDto[];
  columns: WorkloadColumn[];
  shared: Set<string>;
  sort: WorkloadSort;
  onSort: (key: WorkloadSortKey) => void;
  organizationId: string;
  onOpenDeadlines: (person: PersonProcurementWorkloadDto) => void;
}) => {
  const { t } = useTranslation();
  return (
    <HorizontalScrollBox border="1px solid" borderColor="smBorder" rounded="lg">
      <Table size="sm" whiteSpace="nowrap">
        <Thead>
          <Tr>
            <SortableHeader
              sortKey="name"
              label={t("Responsible")}
              sort={sort}
              onSort={onSort}
              thProps={stickyNameColumn}
            />
            {columns.map((column) => (
              <SortableHeader
                key={column}
                sortKey={column}
                label={columnLabel(t, column)}
                sort={sort}
                onSort={onSort}
                thProps={{ isNumeric: true }}
              />
            ))}
          </Tr>
        </Thead>
        <Tbody>
          {people.map((person) => (
            <Tr key={person.personId} data-testid={`workload-${person.personId}`}>
              <Td {...stickyNameColumn}>
                <Flex gap="2" align="center" wrap="wrap">
                  <Text color="smText">{person.name}</Text>
                  {!person.active && (
                    <Tag size="sm" colorScheme="gray">
                      {t("Deactivated")}
                    </Tag>
                  )}
                </Flex>
                {sharesName(shared, person) && (
                  <Text fontSize="xs" color="smMuted" overflowWrap="anywhere">
                    {person.email}
                  </Text>
                )}
              </Td>
              {columns.map((column) => (
                <Td key={column} isNumeric p="0">
                  <CountCell
                    person={person}
                    column={column}
                    organizationId={organizationId}
                    onOpenDeadlines={onOpenDeadlines}
                    t={t}
                  />
                </Td>
              ))}
            </Tr>
          ))}
        </Tbody>
      </Table>
    </HorizontalScrollBox>
  );
};

const SortableHeader = ({
  sortKey,
  label,
  sort,
  onSort,
  thProps,
}: {
  sortKey: WorkloadSortKey;
  label: string;
  sort: WorkloadSort;
  onSort: (key: WorkloadSortKey) => void;
  thProps?: TableColumnHeaderProps;
}) => {
  const direction = sort?.key === sortKey ? sort.direction : undefined;
  return (
    <ColumnHeader
      column={{
        key: sortKey,
        label,
        onSort: () => onSort(sortKey),
        currentSortDirection:
          direction === "ascending" ? SortDirection.Asc : direction === "descending" ? SortDirection.Desc : undefined,
      }}
      thProps={{ "aria-sort": direction ?? "none", ...thProps }}
    />
  );
};

const CountCell = ({
  person,
  column,
  organizationId,
  onOpenDeadlines,
  t,
}: {
  person: PersonProcurementWorkloadDto;
  column: WorkloadColumn;
  organizationId: string;
  onOpenDeadlines: (person: PersonProcurementWorkloadDto) => void;
  t: TFunction;
}) => {
  const count = countOf(person, column);
  const shown = formatNumber(count);
  if (count === 0)
    return (
      <Text color="smMuted" {...countTarget}>
        {shown}
      </Text>
    );
  const label = String(t("{{count}} for {{name}}: {{column}}", { count, name: person.name, column: columnLabel(t, column) }));
  const look = { ...countTarget, color: "smPrimary", fontWeight: "semibold" } as const;
  if (column === "deadlines")
    return (
      <Button
        variant="link"
        {...look}
        h="auto"
        aria-haspopup="dialog"
        aria-label={label}
        onClick={() => onOpenDeadlines(person)}
      >
        {shown}
      </Button>
    );
  return (
    <Link {...look} as={RouterLink} to={workloadLink(person, column, organizationId)} aria-label={label}>
      {shown}
    </Link>
  );
};

const DeadlinesDialog = ({
  person,
  organizationId,
  onClose,
}: {
  person: PersonProcurementWorkloadDto;
  organizationId: string;
  onClose: () => void;
}) => {
  const { t } = useTranslation();
  const { message } = useApiError();
  const { data, isLoading, isFetching, error, refetch } = useListProcurementWorkloadDeadlinesQuery(
    { orgId: organizationId, pathPersonId: person.personId },
  );

  return (
    <Modal isOpen onClose={onClose} size="lg" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader pr="12">{t("Deadlines next 30 days for {{name}}", { name: person.name })}</ModalHeader>
        <ModalCloseButton />
        <ModalBody pb="6">
          {isLoading ? (
            <Spinner color="smPrimary" />
          ) : error || !data ? (
            <ListLoadError message={message(error)} onRetry={() => void refetch()} isRetrying={isFetching} />
          ) : data.deadlines.length === 0 ? (
            <Text color="smMuted">{t("No deadlines in the next 30 days any more.")}</Text>
          ) : (
            <Stack as="ul" listStyleType="none" spacing="3" data-testid="workload-deadlines">
              {data.deadlines.map((deadline) => (
                <Box as="li" key={`${deadline.subjectId}-${deadline.kind}-${deadline.dueAt}`}>
                  <DeadlineRow deadline={deadline} />
                </Box>
              ))}
            </Stack>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
